import { Box, Typography } from '@mui/material';
import React from 'react';
import styled from '@emotion/styled';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import ElectricBoltOutlinedIcon from '@mui/icons-material/ElectricBoltOutlined';
import { RootState } from 'store';
import { useSelector } from 'react-redux';

const TopLine = styled('div')`
	position: absolute;
	top: -2px;
	left: 16.5px;
	width: 1px;
	height: 25px;
	background-color: rgb(138, 140, 146);
	transform: rotate(45deg);
	transform-origin: left top;
	z-index: 13;
`;

const BottomLine = styled('div')`
	position: absolute;
	width: 1px;
	height: 25px;
	background-color: rgb(138, 140, 146);
	transform: rotate(45deg);
	transform-origin: left top;
	z-index: 13;
	bottom: -9px;
	right: -0.5px;
`;

const GridItem = styled('div')`
	display: flex;
	flex-direction: row;
	align-items: center;
	background-color: #fff;
	border-radius: 10px;
	position: relative;
	height: 100%;
	padding: 20px;
	border: 1px solid #ddd;
`;

const Grid = styled('div')`
	display: grid;
	width: 100%;
	grid-template-columns: repeat(4, 1fr);
	padding: 40px 0 0 0;
	column-gap: 20px;
	max-width: 1250px;

	@media (max-width: 1100px) {
		grid-template-columns: repeat(2, 1fr);
		column-gap: 10px;
		row-gap: 30px;
	}
	@media (max-width: 500px) {
		grid-template-columns: repeat(1, 1fr);
		column-gap: 10px;
		row-gap: 10px;
	}
`;

const Row = styled('div')`
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: start;
`;

const Column = styled(Row)`
	flex-direction: column;
	align-items: start;
	justify-content: start;
`;

function formatUSD(n: number) {
	if (!Number.isFinite(n)) return '$0.00';
	return n.toLocaleString('en-US', {
		style: 'currency',
		currency: 'USD',
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	});
}

function formatPct(n: number) {
	if (!Number.isFinite(n)) return '0%';
	return `${n.toLocaleString('en-US', {
		maximumFractionDigits: 1,
		minimumFractionDigits: 1,
	})}%`;
}

export const TVLHeader: React.FC<{
	contractBal: string;
}> = ({ contractBal }) => {
	const depositsNum = Number.parseFloat(contractBal);
	const selectedVault = useSelector((state: RootState) => state.vault);

	const cards = [
		{
			id: 0,
			Icon: AttachMoneyIcon,
			title: 'Total TVL',
			value: `7200 ${selectedVault.token[0].name}`,
			subLabel: 'Locked in vaults',
			color: '#000',
		},
		{
			id: 1,
			Icon: TrendingUpIcon,
			title: 'Current APY',
			value: formatPct(7.1),
			subLabel: 'Annual yield',
			color: '#009d2a',
		},
		{
			id: 2,
			Icon: ShieldOutlinedIcon,
			title: 'Your Deposits',
			value: `${contractBal} ${selectedVault.token[0].name}`,
			subLabel: depositsNum > 0 ? 'Your deposit' : 'No deposits yet',
			color: '#000',
		},
		{
			id: 3,
			Icon: ElectricBoltOutlinedIcon,
			title: 'Total Earnings',
			value: '-',
			subLabel: 'Start earning today',
			color: '#000',
		},
	] as const;

	return (
		<Grid>
			{cards.map(({ id, Icon, title, value, subLabel, color }) => (
				<GridItem key={id}>
					<Column>
						<Row>
							<Icon fontSize="small" />
							<Typography
								variant="caption"
								color="#1b1f23"
								style={{ marginLeft: 10, fontSize: 12 }}
							>
								{title}
							</Typography>
						</Row>

						<Box height={20} />
						<Typography
							variant="h5"
							fontWeight={700}
							style={{ color }}
						>
							{value}
						</Typography>
						<Box height={20} />
						<Typography
							variant="body2"
							color="#6b7280"
							style={{ fontSize: 12 }}
						>
							{subLabel}
						</Typography>
					</Column>
				</GridItem>
			))}
		</Grid>
	);
};
